import { Heart } from 'lucide-react';
import {
  familyOf,
  type CoupleGoal,
  type CoupleLevel,
  type DateSpot,
  type FamilyContent,
  type WeddingTier,
  type WeddingVenue,
} from '@london-life/contracts';
import { Button } from '@london-life/ui/components/button';
import { Input } from '@london-life/ui/components/input';
import { Switch } from '@london-life/ui/components/switch';
import { PageHeader } from '@london-life/ui/admin/page-header';
import { ErrorNotice } from '@london-life/ui/feedback/error-notice';
import { useAdmin } from '../../lib/admin-context';
import { useContent } from './use-content';
import { ContentBar } from './versions';
import { Field, NumberField, PlaceSelect, idFor, needSpec, type NumberSpec } from './form';
import { FamilyChildrenSections } from './family-children';

type FamilyNumbers =
  'maxPartners' | 'proposeCloseness' | 'engagedHours' | 'ringPence' | 'askHours' | 'dateEveryHours' | 'anniversaryDays';
const RULES: NumberSpec<FamilyNumbers>[] = [
  { key: 'maxPartners', label: 'Partners at once while dating', unit: 'number', min: 1, max: 10, whole: true },
  {
    key: 'proposeCloseness',
    label: 'Closeness needed to propose (0 to 100)',
    unit: 'number',
    min: 0,
    max: 100,
    whole: true,
  },
  { key: 'ringPence', label: 'The ring', unit: 'pounds', min: 0, max: 100_000_000 },
  {
    key: 'engagedHours',
    label: 'Engaged this many hours before the wedding',
    unit: 'number',
    min: 0,
    max: 720,
    whole: true,
  },
  {
    key: 'askHours',
    label: 'An ask waits this many hours for an answer',
    unit: 'number',
    min: 1,
    max: 168,
    whole: true,
  },
  {
    key: 'dateEveryHours',
    label: 'One date a couple every this many hours',
    unit: 'number',
    min: 0,
    max: 168,
    whole: true,
  },
  {
    key: 'anniversaryDays',
    label: 'An anniversary every this many days',
    unit: 'number',
    min: 1,
    max: 365,
    whole: true,
  },
];
const PRICE: NumberSpec<'pricePence'> = {
  key: 'pricePence',
  label: 'Price',
  unit: 'pounds',
  min: 0,
  max: 1_000_000_000,
};
const CLOSER: NumberSpec<'closeness'> = {
  key: 'closeness',
  label: 'Closer by',
  unit: 'number',
  min: 0,
  max: 30,
  whole: true,
};
const GUESTS: NumberSpec<'guests'> = {
  key: 'guests',
  label: 'Guests',
  unit: 'number',
  min: 2,
  max: 1_000,
  whole: true,
};
const AT: NumberSpec<'at'> = {
  key: 'at',
  label: 'From this many points',
  unit: 'number',
  min: 0,
  max: 100,
  whole: true,
};
// the steps of a goal typed as "3, 10, 25"
const stepsText = (steps: number[]) => steps.join(', ');
const stepsOf = (text: string) =>
  text
    .split(/[,\s]+/)
    .map(Number)
    .filter((step) => Number.isInteger(step) && step >= 1 && step <= 10_000)
    .slice(0, 10);

// Family (as Lagos Life's Family): the rules for partners, the date spots, the wedding's tiers and venues, the
// couple goals and levels, then children and relatives (family-children.tsx). Part of the content draft, published with it. Date spots and venues are places on
// the map (chosen from the Places page's places).
export function FamilyPage() {
  const { can } = useAdmin();
  const editor = useContent();
  const working = editor.working;
  const readOnly = !can('content.write');
  const family = familyOf(working ?? {});
  const places = working?.places ?? [];
  const change = (fn: (draft: FamilyContent) => void) =>
    editor.edit((content) => {
      content.family ??= structuredClone(familyOf(content));
      fn(content.family);
    });
  const spot = (id: string, fn: (entry: DateSpot) => void) =>
    change((draft) => {
      const entry = draft.dateSpots.find((item) => item.id === id);
      if (entry) fn(entry);
    });
  const tier = (id: string, fn: (entry: WeddingTier) => void) =>
    change((draft) => {
      const entry = draft.weddingTiers.find((item) => item.id === id);
      if (entry) fn(entry);
    });
  const venue = (index: number, fn: (entry: WeddingVenue) => void) =>
    change((draft) => {
      const entry = draft.weddingVenues[index];
      if (entry) fn(entry);
    });
  const goal = (key: CoupleGoal['key'], fn: (entry: CoupleGoal) => void) =>
    change((draft) => {
      const entry = draft.goals.find((item) => item.key === key);
      if (entry) fn(entry);
    });
  const level = (index: number, fn: (entry: CoupleLevel) => void) =>
    change((draft) => {
      const entry = draft.levels[index];
      if (entry) fn(entry);
    });
  return (
    <section>
      <PageHeader
        title="Family"
        description="Partners and couples (as Lagos Life's Family): the rules, the dates, the ring, the wedding, the couple goals, babies and relatives. Changes are a draft until they are published."
      />
      <ContentBar editor={editor} />
      {editor.failure && <ErrorNotice error={editor.failure} onRetry={() => void editor.reload()} />}
      {!working && !editor.failure && <p className="admin-muted">Loading…</p>}
      {working && (
        <div className="content-editor">
          <fieldset className="admin-panel content-section" disabled={readOnly} aria-label="The rules">
            <h2 className="content-subtitle">
              <Heart size={16} aria-hidden="true" /> The rules
            </h2>
            <label className="designer-check">
              <Switch
                checked={family.visible}
                aria-label="Family open"
                onCheckedChange={(on) => change((draft) => void (draft.visible = on))}
              />{' '}
              Players can use Family (dates, the ring, the wedding)
            </label>
            <div className="content-grid">
              {RULES.map((spec) => (
                <NumberField
                  key={spec.key}
                  id={`family-${spec.key}`}
                  spec={spec}
                  value={family[spec.key]}
                  onChange={(value) => change((draft) => void (draft[spec.key] = value))}
                />
              ))}
              {(['fun', 'social'] as const).map((key) => (
                <NumberField
                  key={key}
                  id={`family-wedding-${key}`}
                  spec={needSpec(key, `The wedding: ${key} for both`)}
                  value={family.weddingEffects[key] ?? 0}
                  onChange={(value) => change((draft) => void (draft.weddingEffects[key] = value))}
                />
              ))}
            </div>
          </fieldset>

          <fieldset className="admin-panel content-section" disabled={readOnly} aria-label="Dates">
            <h2 className="content-subtitle">Dates</h2>
            {family.dateSpots.map((item) => (
              <div className="content-grid content-row" key={item.id} data-testid={`date-${item.id}`}>
                <Field id={`date-${item.id}-label`} label="Date">
                  <Input
                    id={`date-${item.id}-label`}
                    value={item.label}
                    maxLength={50}
                    onChange={(e) => spot(item.id, (entry) => void (entry.label = e.target.value))}
                  />
                </Field>
                <Field id={`date-${item.id}-emoji`} label="Emoji">
                  <Input
                    id={`date-${item.id}-emoji`}
                    value={item.emoji}
                    maxLength={16}
                    onChange={(e) => spot(item.id, (entry) => void (entry.emoji = e.target.value))}
                  />
                </Field>
                <PlaceSelect
                  id={`date-${item.id}-place`}
                  label="Where"
                  value={item.placeId}
                  places={places}
                  onChange={(placeId) => spot(item.id, (entry) => void (entry.placeId = placeId))}
                />
                <NumberField
                  id={`date-${item.id}-price`}
                  spec={PRICE}
                  value={item.pricePence}
                  onChange={(value) => spot(item.id, (entry) => void (entry.pricePence = value))}
                />
                {(['fun', 'social'] as const).map((key) => (
                  <NumberField
                    key={key}
                    id={`date-${item.id}-${key}`}
                    spec={needSpec(key, key === 'fun' ? 'Fun for both' : 'Social for both')}
                    value={item.effects[key] ?? 0}
                    onChange={(value) => spot(item.id, (entry) => void (entry.effects[key] = value))}
                  />
                ))}
                <NumberField
                  id={`date-${item.id}-closeness`}
                  spec={CLOSER}
                  value={item.closeness}
                  onChange={(value) => spot(item.id, (entry) => void (entry.closeness = value))}
                />
                <label className="designer-check">
                  <Switch
                    checked={item.visible}
                    aria-label={`${item.label} on`}
                    onCheckedChange={(on) => spot(item.id, (entry) => void (entry.visible = on))}
                  />{' '}
                  On
                </label>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              onClick={() =>
                change((draft) =>
                  draft.dateSpots.push({
                    id: idFor('New date', new Set(draft.dateSpots.map((entry) => entry.id)), 'date', 30),
                    placeId: places[0]?.id ?? 'park',
                    label: 'New date',
                    emoji: '💕',
                    pricePence: 2_000,
                    effects: { fun: 15, social: 15 },
                    closeness: 5,
                    visible: false,
                  }),
                )
              }
            >
              Add a date
            </Button>
          </fieldset>

          <fieldset className="admin-panel content-section" disabled={readOnly} aria-label="The wedding">
            <h2 className="content-subtitle">The wedding</h2>
            {family.weddingTiers.map((item) => (
              <div className="content-grid content-row" key={item.id} data-testid={`tier-${item.id}`}>
                <Field id={`tier-${item.id}-label`} label="Wedding">
                  <Input
                    id={`tier-${item.id}-label`}
                    value={item.label}
                    maxLength={40}
                    onChange={(e) => tier(item.id, (entry) => void (entry.label = e.target.value))}
                  />
                </Field>
                <Field id={`tier-${item.id}-emoji`} label="Emoji">
                  <Input
                    id={`tier-${item.id}-emoji`}
                    value={item.emoji}
                    maxLength={16}
                    onChange={(e) => tier(item.id, (entry) => void (entry.emoji = e.target.value))}
                  />
                </Field>
                <Field id={`tier-${item.id}-line`} label="What it is like">
                  <Input
                    id={`tier-${item.id}-line`}
                    value={item.line}
                    maxLength={120}
                    onChange={(e) => tier(item.id, (entry) => void (entry.line = e.target.value))}
                  />
                </Field>
                <NumberField
                  id={`tier-${item.id}-price`}
                  spec={PRICE}
                  value={item.pricePence}
                  onChange={(value) => tier(item.id, (entry) => void (entry.pricePence = value))}
                />
                <NumberField
                  id={`tier-${item.id}-guests`}
                  spec={GUESTS}
                  value={item.guests}
                  onChange={(value) => tier(item.id, (entry) => void (entry.guests = value))}
                />
                <label className="designer-check">
                  <Switch
                    checked={item.visible}
                    aria-label={`${item.label} on`}
                    onCheckedChange={(on) => tier(item.id, (entry) => void (entry.visible = on))}
                  />{' '}
                  On
                </label>
              </div>
            ))}
            <h3 className="content-mini-title">Venues</h3>
            {family.weddingVenues.map((item, index) => (
              <div className="content-grid content-row" key={`${item.placeId}-${index}`} data-testid={`venue-${index}`}>
                <PlaceSelect
                  id={`venue-${index}-place`}
                  label="Place"
                  value={item.placeId}
                  places={places}
                  onChange={(placeId) => venue(index, (entry) => void (entry.placeId = placeId))}
                />
                <Field id={`venue-${index}-label`} label="Called">
                  <Input
                    id={`venue-${index}-label`}
                    value={item.label}
                    maxLength={50}
                    onChange={(e) => venue(index, (entry) => void (entry.label = e.target.value))}
                  />
                </Field>
                <Field id={`venue-${index}-emoji`} label="Emoji">
                  <Input
                    id={`venue-${index}-emoji`}
                    value={item.emoji}
                    maxLength={16}
                    onChange={(e) => venue(index, (entry) => void (entry.emoji = e.target.value))}
                  />
                </Field>
                <label className="designer-check">
                  <Switch
                    checked={item.visible}
                    aria-label={`${item.label} on`}
                    onCheckedChange={(on) => venue(index, (entry) => void (entry.visible = on))}
                  />{' '}
                  On
                </label>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              onClick={() =>
                change((draft) =>
                  draft.weddingVenues.push({
                    placeId:
                      places.find((place) => !draft.weddingVenues.some((v) => v.placeId === place.id))?.id ?? 'park',
                    label: 'New venue',
                    emoji: '💒',
                    visible: false,
                  }),
                )
              }
            >
              Add a venue
            </Button>
          </fieldset>

          <fieldset className="admin-panel content-section" disabled={readOnly} aria-label="Couple goals and levels">
            <h2 className="content-subtitle">Couple goals and levels</h2>
            {family.goals.map((item) => (
              <div className="content-grid content-row" key={item.key} data-testid={`goal-${item.key}`}>
                <Field id={`goal-${item.key}-label`} label="Goal">
                  <Input
                    id={`goal-${item.key}-label`}
                    value={item.label}
                    maxLength={40}
                    onChange={(e) => goal(item.key, (entry) => void (entry.label = e.target.value))}
                  />
                </Field>
                <Field id={`goal-${item.key}-emoji`} label="Emoji">
                  <Input
                    id={`goal-${item.key}-emoji`}
                    value={item.emoji}
                    maxLength={16}
                    onChange={(e) => goal(item.key, (entry) => void (entry.emoji = e.target.value))}
                  />
                </Field>
                <Field id={`goal-${item.key}-steps`} label="Steps (each is a point)">
                  <Input
                    id={`goal-${item.key}-steps`}
                    defaultValue={stepsText(item.steps)}
                    onBlur={(e) => {
                      const steps = stepsOf(e.target.value);
                      if (steps.length) goal(item.key, (entry) => void (entry.steps = steps));
                      e.target.value = stepsText(steps.length ? steps : item.steps);
                    }}
                  />
                </Field>
              </div>
            ))}
            <h3 className="content-mini-title">Levels</h3>
            {family.levels.map((item, index) => (
              <div className="content-grid content-row" key={index} data-testid={`level-${index}`}>
                <Field id={`level-${index}-title`} label="Title">
                  <Input
                    id={`level-${index}-title`}
                    value={item.title}
                    maxLength={40}
                    onChange={(e) => level(index, (entry) => void (entry.title = e.target.value))}
                  />
                </Field>
                <Field id={`level-${index}-emoji`} label="Emoji">
                  <Input
                    id={`level-${index}-emoji`}
                    value={item.emoji}
                    maxLength={16}
                    onChange={(e) => level(index, (entry) => void (entry.emoji = e.target.value))}
                  />
                </Field>
                <NumberField
                  id={`level-${index}-at`}
                  spec={AT}
                  value={item.at}
                  onChange={(value) => level(index, (entry) => void (entry.at = value))}
                />
              </div>
            ))}
          </fieldset>

          <FamilyChildrenSections family={family} change={change} readOnly={readOnly} />
        </div>
      )}
    </section>
  );
}
